import { useEffect, useState } from "react";
import { InputNumber } from "primereact/inputnumber";
import localeText from "../../../../locale";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";
import { getErrorCode } from "../../hooks/useAdminList";
import { redirectIfUnauthorized } from "../../utils/adminUtils";
import FormField from "../../components/form/FormField";
import EmptyState from "../../components/list/EmptyState";
import ConfirmActionDialog from "../../components/list/ConfirmActionDialog";
import {
  BANNER_TYPES,
  emptyBanner,
  hasAnyImage,
  loadBannerConfig,
  saveBannerConfig,
} from "./bannerModel";
import BannerCard from "./components/BannerCard";
import CropDialog from "./components/CropDialog";
import BannerPreview from "./components/BannerPreview";
import "../AdminHome/AdminHome.css";
import "./AdminBanners.css";

const readAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// Banner principal (type="main") e sub banners 1 a 3 (type="sub1" | "sub2" | "sub3")
const AdminBanners = ({ type }) => {
  const config = BANNER_TYPES[type];
  const { showErrorToast, showSuccessToast } = useToastContext();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [banners, setBanners] = useState([]);
  const [delays, setDelays] = useState({});
  const [removedIds, setRemovedIds] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [crop, setCrop] = useState(null);
  const [toRemove, setToRemove] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await loadBannerConfig(type);
      setBanners(data.banners);
      setDelays({
        delayMainBanner: data.delays.delayMainBanner || 1,
        delaySubBanners: data.delays.delaySubBanners || 1,
      });
      setRemovedIds([]);
      setDirty(false);
      setLoadError(false);
    } catch (e) {
      if (redirectIfUnauthorized(e)) return;
      console.log(e);
      setLoadError(true);
      showErrorToast({ code: 500000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  // Avisa antes de sair da página com alterações não salvas
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const updateBanner = (key, changes) => {
    setBanners((prev) => prev.map((banner) => (banner.key === key ? { ...banner, ...changes } : banner)));
    setDirty(true);
  };

  const pickImage = async (bannerKey, field, file) => {
    try {
      setCrop({ bannerKey, field, src: await readAsDataUrl(file) });
    } catch (e) {
      showErrorToast({ detail: "unexpectedErrorUploadImg" });
    }
  };

  const applyCrop = (file, fields) => {
    setBanners((prev) =>
      prev.map((banner) =>
        banner.key === crop.bannerKey
          ? {
              ...banner,
              images: { ...banner.images, ...Object.fromEntries(fields.map((field) => [field, file])) },
            }
          : banner,
      ),
    );
    setDirty(true);
    setCrop(null);
  };

  const addBanner = () => {
    setBanners((prev) => [...prev, emptyBanner()]);
    setDirty(true);
  };

  const removeBanner = () => {
    const banner = toRemove;
    if (banner.original?.id) setRemovedIds((prev) => [...prev, banner.original.id]);
    setBanners((prev) => prev.filter((item) => item.key !== banner.key));
    setDirty(true);
    setToRemove(null);
  };

  const save = async () => {
    if (!hasAnyImage(banners[0])) {
      showErrorToast({ detail: "noImageSelected" });
      return;
    }
    setSaving(true);
    try {
      await saveBannerConfig({ type, banners, removedIds, delays });
      showSuccessToast({ detail: "settingsSaved" });
      await load();
    } catch (e) {
      if (redirectIfUnauthorized(e)) return;
      console.log(e);
      const status = e?.response?.status ?? e?.status;
      if (status === 500) showErrorToast({ detail: "unexpectedSystemBehaviour" });
      else if (e?.code) showErrorToast({ code: getErrorCode(e) });
      else showErrorToast({ detail: "unexpectedErrorUploadImg" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-banners">
        <div className="admin-banner-card admin-banners__loading">
          <span className="admin-home-skeleton admin-home-skeleton--value" />
          <span className="admin-home-skeleton admin-banners__skeleton-grid" />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="admin-banner-card">
        <EmptyState
          icon="pi pi-exclamation-triangle"
          message={localeText("adminListLoadError")}
          actionLabel={localeText("adminUsersRetry")}
          onAction={load}
        />
      </div>
    );
  }

  const delayField = config.delayField;

  return (
    <div className="admin-banners">
      <CropDialog
        crop={crop}
        ratio={config.ratio}
        ratioLabel={config.ratioLabel}
        onApply={applyCrop}
        onCancel={() => setCrop(null)}
      />

      <ConfirmActionDialog
        visible={!!toRemove}
        title={localeText("removeBanner")}
        message={localeText("adminBannersRemoveMessage")}
        confirmLabel={localeText("removeBanner")}
        confirmTone="danger"
        onConfirm={removeBanner}
        onCancel={() => setToRemove(null)}
      />

      <section className="admin-banner-card admin-banners__settings">
        <FormField
          label={localeText("sliderChangeTime")}
          htmlFor="banner-delay"
          hint={type === "main" ? null : localeText("adminBannersSubDelayHint")}
        >
          <div className="admin-banners__delay">
            <InputNumber
              inputId="banner-delay"
              value={delays[delayField]}
              onValueChange={(e) => {
                setDelays((prev) => ({ ...prev, [delayField]: e.value || 1 }));
                setDirty(true);
              }}
              min={1}
              max={60}
              showButtons
              buttonLayout="horizontal"
              incrementButtonIcon="pi pi-plus"
              decrementButtonIcon="pi pi-minus"
            />
            <span className="admin-muted">{localeText("seconds")}</span>
          </div>
        </FormField>
        <p className="admin-muted admin-banners__ratio">
          <i className="pi pi-info-circle" aria-hidden="true" />
          {`${localeText("adminBannersRatioHint")} ${config.ratioLabel}.`}
        </p>
      </section>

      {banners.map((banner, index) => (
        <BannerCard
          key={banner.key}
          banner={banner}
          index={index}
          ratio={config.ratio}
          onChange={(changes) => updateBanner(banner.key, changes)}
          onPickImage={(field, file) => pickImage(banner.key, field, file)}
          onInvalidImage={(message) => showErrorToast({ detail: message })}
          onRemove={() => setToRemove(banner)}
        />
      ))}

      <button type="button" className="admin-banners__add" onClick={addBanner}>
        <i className="pi pi-plus" aria-hidden="true" />
        {localeText("addNewBanner")}
      </button>

      <BannerPreview type={type} banners={banners} delay={delays[delayField]} />

      <div className="admin-banners__savebar">
        <span className="admin-muted">
          {dirty ? (
            <>
              <i className="pi pi-circle-fill admin-banners__dirty-dot" aria-hidden="true" />
              {localeText("adminBannersUnsaved")}
            </>
          ) : (
            localeText("adminBannersAllSaved")
          )}
        </span>
        <button
          type="button"
          className="admin-btn admin-btn--primary"
          onClick={save}
          disabled={saving || !dirty}
        >
          {saving && <i className="pi pi-spin pi-spinner" aria-hidden="true" />}
          {localeText(saving ? "adminBannersSaving" : "save")}
        </button>
      </div>
    </div>
  );
};

export default AdminBanners;
